import { Fragment } from 'react';
import type { KeyLevelTone } from '@/types/session';
import { DashboardPanel } from './DashboardPanel';
import { RailSkeleton } from './RailSkeleton';
import { RailStateBlock } from './RailStateBlock';
import { formatExposureSnapshotTime, formatSessionDateLabel } from './dashboardFormat';
import type { RailPanelState } from './railPanelState.utils';

interface KeyLevelsPanelProps {
  state: RailPanelState;
  sessionDate: string;
  onRetry: () => void;
}

const VALUE_TONE_CLASS_NAME: Record<KeyLevelTone, string> = {
  positive: 'text-up',
  negative: 'text-down',
  warning: 'text-gold',
  neutral: 'text-softer',
};

export function KeyLevelsPanel({ state, sessionDate, onRetry }: KeyLevelsPanelProps) {
  return (
    <DashboardPanel
      title="Key Levels"
      subtitle={
        state.kind === 'ready' && state.analytics.dataAvailability.exposure.status === 'available'
          ? `${formatSessionDateLabel(sessionDate)} · ${exposureSubtitle(state.analytics.exposureSource, state.analytics.exposureUpdatedAt)}`
          : formatSessionDateLabel(sessionDate)
      }
    >
      {state.kind === 'loading' ? (
        <RailSkeleton className="mt-3.5 h-[130px]" />
      ) : state.kind === 'error' ? (
        <RailStateBlock
          tone="error"
          title="Key levels unavailable"
          message={state.message}
          onRetry={onRetry}
        />
      ) : state.analytics.keyLevels.length === 0 ? (
        <RailStateBlock
          title="No key levels"
          message="No key levels are available for this session."
        />
      ) : (
        <dl className="mt-2 grid grid-cols-[1fr_auto_auto] items-baseline gap-x-3 gap-y-[5px] text-[13px] text-fg">
          {state.analytics.keyLevels.map((level) => (
            <Fragment key={level.label}>
              <dt>
                <OrdinalLabel label={level.label} />
              </dt>
              <dd className={`text-right font-mono ${level.tone === 'warning' ? 'text-gold' : ''}`}>
                {level.strikeLabel.replace(' - ', ' – ')}
              </dd>
              <dd className={`text-right font-mono ${VALUE_TONE_CLASS_NAME[level.tone]}`}>
                {level.valueLabel}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
    </DashboardPanel>
  );
}

function exposureSubtitle(
  source: 'live' | 'saved' | 'unavailable' | undefined,
  observedAt: string,
): string {
  const observedTime = formatExposureSnapshotTime(observedAt);
  return source === 'saved'
    ? `Closing snapshot observed ${observedTime}`
    : `Current exposure observed ${observedTime}`;
}

function OrdinalLabel({ label }: { label: string }) {
  const match = /^(\d+)(st|nd|rd|th)\b(.*)$/.exec(label);
  if (match == null) {
    return <>{label}</>;
  }
  return (
    <>
      {match[1]}
      <sup>{match[2]}</sup>
      {match[3]}
    </>
  );
}
